// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import React, { FC } from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Starter Custom Code - End */

interface OwnProps {
    extendedStyles?: ProductQuantityBreakPricingStyles;
    // Starter Custom Code
    overridedStyles?: ProductQuantityBreakPricingStyles;
}

export interface ProductQuantityBreakPricingStyles {
    viewLink?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    table?: DataTableProps;
    tableHead?: DataTableHeadProps;
    breakQtyHeader?: DataTableHeaderProps;
    breakPriceHeader?: DataTableHeaderProps;
    savingsHeader?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    breakQtyCell?: DataTableCellBaseProps;
    breakPriceCell?: DataTableCellBaseProps;
    savingsCell?: DataTableCellBaseProps;
    // Starter Custom Code
    wrapper?: InjectableCss;
}

export const productQuantityBreakPricingStyles: ProductQuantityBreakPricingStyles = {
    modal: {
        sizeVariant: "small",
        cssOverrides: {
            modalContent: css`
                padding: ${pxToRem(10)};
            `,
        },
    },
    breakQtyHeader: { alignX: "center" },
    breakPriceHeader: { alignX: "center" },
    savingsHeader: { alignX: "center" },
    breakQtyCell: { alignX: "center" },
    breakPriceCell: { alignX: "center" },
    savingsCell: { alignX: "center" },
    wrapper: {
        css: css`
            display: flex;
        `,
    },
    viewLink: {
        css: linkActionStyles(),
    },
    table: {
        css: css`
            ${x => x.theme.dataTable.defaultProps?.css}
            border: none;
        `,
    },
};

const ProductQuantityBreakPricing: FC<OwnProps & HasProduct> = ({
    product,
    productInfo: { pricing },
    extendedStyles,
    // Starter Custom Code
    overridedStyles,
}) => {
    const showPricing = useAppSelector(state => state.context.showPricing);
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewLinkClickHandler = () => {
        setModalIsOpen(true);
    };

    const styles = useExpandProps(productQuantityBreakPricingStyles, extendedStyles, overridedStyles); // Starter Custom Code

    if (
        !product ||
        product.quoteRequired ||
        !pricing ||
        !pricing.unitRegularBreakPrices ||
        pricing.unitRegularBreakPrices.length < 2 ||
        !showPricing
    ) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper /* Starter Custom Code */}>
            <Link {...styles.viewLink} onClick={viewLinkClickHandler} data-test-selector="quantityBreakPricingLink">
                {translate("View Quantity Break Pricing")}
            </Link>
            <Modal
                {...styles.modal}
                headline={translate("Quantity Pricing")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
                data-test-selector="quantityBreakPricingModal"
            >
                <DataTable {...styles.table}>
                    <DataTableHead {...styles.tableHead}>
                        <DataTableHeader {...styles.breakQtyHeader}>{translate("Min Qty")}</DataTableHeader>
                        <DataTableHeader {...styles.breakPriceHeader}>{translate("Price Per")}</DataTableHeader>
                        <DataTableHeader {...styles.savingsHeader} title={translate("Savings")} />
                    </DataTableHead>
                    <DataTableBody {...styles.tableBody}>
                        {pricing!.unitRegularBreakPrices!.map(breakPrice => (
                            <DataTableRow
                                {...styles.tableRow}
                                key={breakPrice.breakQty}
                                data-test-selector="breakPriceRow"
                            >
                                <DataTableCell {...styles.breakQtyCell} data-test-selector="breakQty">
                                    {breakPrice.breakQty}
                                </DataTableCell>
                                <DataTableCell {...styles.breakPriceCell} data-test-selector="price">
                                    {breakPrice.breakPriceDisplay}
                                </DataTableCell>
                                <DataTableCell {...styles.savingsCell} data-test-selector="message">
                                    {breakPrice.savingsMessage}
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            </Modal>
        </StyledWrapper>
    );
};

export default withProduct(ProductQuantityBreakPricing);
